import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelActionRow,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatCard, MatCardContent, MatCardHeader} from "@angular/material/card";
import {DatePipe, DecimalPipe, NgClass} from "@angular/common";
import {TeamPipe} from "../../pipes/team-pipe";
import {TournamentService} from "../../service/tournament.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Tournament} from "../../model/tournament";
import {FullNamePipe} from "../../pipes/fullname-pipe";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {Group} from "../../model/group";
import {Round} from "../../model/round";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {Match} from "../../model/match";
import {FormsModule} from "@angular/forms";
import {MatTab, MatTabChangeEvent, MatTabGroup, MatTabLabel} from "@angular/material/tabs";
import {MatchResultComponent} from "../match-result/match-result.component";
import {MatDialog} from "@angular/material/dialog";
import {MatchResultPipe} from "../../pipes/match-result-pipe";
import {Event} from "../../model/event";
import {TournamentValidateComponent} from "../tournament-validate/tournament-validate.component";
import {Player, Strength} from "../../model/player";
import {MatSnackBar} from "@angular/material/snack-bar";
import {CourtSelectionComponent} from "../court-selection/court-selection.component";
import {Standings} from "../../model/standings";
import {HeaderService} from "../../service/header.service";
import {TournamentPlayersComponent} from "../tournament-players/tournament-players.component";
import {TournamentPlayer} from "../../model/tournamentPlayer";
import {TeamDisplayComponent} from "../team-display/team-display.component";
import {CounterSelectionComponent} from "../counter-selection/counter-selection.component";
import {Observable, Subscription} from "rxjs";
import {MatTooltip} from '@angular/material/tooltip';

@Component({
  selector: 'app-tournament-manage',
  imports: [
    FullNamePipe,
    MatAccordion,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    TeamPipe,
    MatIcon,
    NgClass,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    FormsModule,
    DatePipe,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatButton,
    MatIconButton,
    DecimalPipe,
    TournamentValidateComponent,
    TournamentPlayersComponent,
    MatExpansionPanelActionRow,
    TeamDisplayComponent,
    MatTooltip,
  ],
  providers: [
    FullNamePipe,
    TeamPipe,
    MatchResultPipe
  ],
  templateUrl: './tournament-manage.component.html',
  standalone: true,
  styleUrl: './tournament-manage.component.scss'
})
export class TournamentManageComponent implements OnInit, OnDestroy {

  tournament: Tournament;

  activeRoundTab: number = 0;

  constructor(
    private tournamentService: TournamentService,
    private _snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private headerService: HeaderService,
  ) {
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        this.activeRoundTab = params['tab'];
      }
    })
    this.loadTournament(
      this.tournamentService.getById(Number(id)),
      (tournament) => this.headerService.setTitle(tournament.name)
    );
  }

  ngOnDestroy() {
    this.headerService.clearTitle();
  }

  getDuration(startTime: string | Date): Date {
    const start = new Date(startTime);
    const now = new Date();
    const diffInMs = now.getTime() - start.getTime();

    // Convert milliseconds to a Date object starting from epoch
    // This allows the date pipe to format it as mm:ss
    return new Date(diffInMs);
  }

  onRoundTabChange(event: MatTabChangeEvent) {
    const index = event.index;
    this.router.navigate(
      ['tournaments/' + this.tournament.id + '/manage'],
      {
        relativeTo: null, queryParams: {tab: index}
      });
  }

  getRoundIcon(status: string) {
    if (status == "FINISHED") {
      return "check";
    } else if (status == "IN_PROGRESS") {
      return "play_arrow";
    } else if (status == "NOT_STARTED") {
      return "hourglass_top";
    } else {
      return "warning";
    }
  }

  getStandingsForRound(round: Round, group: Group): Standings {
    if (round.status == 'FINISHED') {
      return round.standings;
    } else {
      return group.standings;
    }
  }

  groupIsDoublesType(group: Group) {
    return group.type == 'HD' || group.type == 'DD' || group.type == 'GD';
  }

  startRound(round: Round) {
    this.loadTournament(this.tournamentService.startRound(this.tournament.id, round.id));
  }

  finishRound(round: Round) {
    this.loadTournament(this.tournamentService.finishRound(this.tournament.id, round.id));
  }

  finishGroup(group: Group) {
    this.loadTournament(this.tournamentService.finishGroup(this.tournament.id, group.id));
  }

  reopenGroup(group: Group) {
    this.loadTournament(this.tournamentService.reopenGroup(this.tournament.id, group.id));
  }

  divideTournament() {
    this.loadTournament(this.tournamentService.divide(this.tournament.id));
  }

  clearDivision() {
    this.loadTournament(this.tournamentService.clearDivision(this.tournament.id));
  }

  drawTournament() {
    this.loadTournament(this.tournamentService.draw(this.tournament.id));
  }

  startMatch(match: Match) {
    const availableCourts = this.getAvailableCourts();

    if (availableCourts.length == 0) {
      this._snackBar.open('Geen banen beschikbaar.')
    } else if (this.matchContainsPlayersThatArePlaying(match)) {
      this._snackBar.open('Deze wedstrijd bevat spelers die al aan het spelen zijn.')
    } else if (this.matchContainsPlayersThatAreCounting(match)) {
      this._snackBar.open('Deze wedstrijd bevat spelers die aan het tellen zijn.')
    } else {
      this.courtSelectionDialog.open(CourtSelectionComponent, {
        data: {
          match: match,
          availableCourts: this.getAvailableCourts(),
          totalCourts: this.tournament.courts,
          availableCounters: this.getAvailableCounters(match)
        },
        minWidth: '800px',
        minHeight: '250px'
      }).afterClosed().subscribe(result => {
        if (result != undefined) {
          this.loadTournament(this.tournamentService.startMatch(this.tournament.id, match.id, result.court, result.counter.playerId));
        }
      });
    }
  }

  matchContainsPlayersThatArePlaying(match: Match): boolean {
    const matchPlayers = this.getMatchPlayers(match);

    let matchPlayersThatArePlaying = this.tournament.playersPlaying.filter(Set.prototype.has, new Set(matchPlayers));
    return matchPlayersThatArePlaying.length > 0;
  }

  matchPlayersThatArePlaying(match: Match): number[] {
    const matchPlayers = this.getMatchPlayers(match);

    return this.tournament.playersPlaying.filter(Set.prototype.has, new Set(matchPlayers));
  }

  matchContainsPlayersThatAreCounting(match: Match): boolean {
    const matchPlayers = this.getMatchPlayers(match);

    let matchPlayersThatAreCounting = this.tournament.playersCounting.filter(Set.prototype.has, new Set(matchPlayers));
    return matchPlayersThatAreCounting.length > 0;
  }

  matchPlayersThatAreCounting(match: Match): number[] {
    const matchPlayers = this.getMatchPlayers(match);

    return this.tournament.playersCounting.filter(Set.prototype.has, new Set(matchPlayers));
  }

  getTournamentPlayerFromPlayer(player: Player): TournamentPlayer {
    for (let tournamentPlayer of this.tournament.tournamentPlayers) {
      if (tournamentPlayer.playerId == player.id) {
        return tournamentPlayer;
      }
    }
    return null!;
  }

  getTournamentPlayerFromId(tournamentPlayerId: number): TournamentPlayer {
    for (let tournamentPlayer of this.tournament.tournamentPlayers) {
      if (tournamentPlayer.id == tournamentPlayerId) {
        return tournamentPlayer;
      }
    }
    return null!;
  }

  getAvailableCourts(): number[] {
    const maxCourts = this.tournament.courts;
    const activeCourts = this.activeMatches().map(activeMatch => activeMatch.match.court);

    let i = 0, courts = Array(maxCourts);
    while (i < maxCourts) courts[i++] = i;

    return courts.filter(court => activeCourts.indexOf(court) < 0);
  }

  getMatchPlayers(match: Match): number[] {
    const matchPlayers: number[] = [];
    matchPlayers.push(this.getPlayerOrSubstitute(match.team1.player1, match.type).id);
    if (match.team1.player2) matchPlayers.push(this.getPlayerOrSubstitute(match.team1.player2, match.type).id);
    matchPlayers.push(this.getPlayerOrSubstitute(match.team2.player1, match.type).id);
    if (match.team2.player2) matchPlayers.push(this.getPlayerOrSubstitute(match.team2.player2, match.type).id);

    return matchPlayers;
  }

  getAvailableCounters(match: Match): TournamentPlayer[] {
    const matchPlayers = this.getMatchPlayers(match);

    const counterIds = this.tournament.playersAvailable.filter(player => !matchPlayers.includes(player));

    return counterIds.map(id => this.getTournamentPlayerFromId(id));
  }


  getPlayerOrSubstitute(player: Player, type: String): TournamentPlayer {
    return this.getSubstituteForEvent(player, type) || this.getTournamentPlayerFromPlayer(player);
  }

  getSubstituteForEvent(player: Player, type: String): TournamentPlayer | undefined {
    const tournamentPlayer = this.tournament.tournamentPlayers.find(
      tp => tp.playerId === player.id
    );
    if (!tournamentPlayer) return undefined;

    const substitution = tournamentPlayer.substitutions.find(
      s => s.event === type
    );
    if (!substitution) return undefined;

    return this.getTournamentPlayerFromId(substitution.substitute);
  }

  stopMatch(match: Match) {
    this.loadTournament(this.tournamentService.stopMatch(this.tournament.id, match.id));
  }

  newRound(group: Group) {
    this.loadTournament(this.tournamentService.newRound(this.tournament.id, group.id));
  }

  getStrength(strength: string | undefined) {
    if (strength == undefined) return "";
    for (let [key, value] of Object.entries(Strength)) {
      if (key == strength) return value;
    }
    return "";
  }

  printMatchSheets(round: Round) {
    window.open(`tournaments/${this.tournament.id}/rounds/${round.id}/matchsheets`, "_blank");
  }

  printRoundOverview(round: Round) {
    window.open(`tournaments/${this.tournament.id}/rounds/${round.id}/overview`, "_blank");
  }

  activeMatches(): ActiveMatch[] {
    let matches: ActiveMatch[] = [];
    for (const event of this.tournament.events) {
      for (const group of event.groups) {
        for (const round of group.rounds) {
          for (const match of round.matches) {
            if (match.status == 'IN_PROGRESS') {
              matches.push(new ActiveMatch(match, round, group, event));
            }
          }
        }
      }
    }
    return matches;
  }

  getActiveMatchCountForGroup(group: Group): number {
    let active = 0;
    for (const round of group.rounds) {
      for (const match of round.matches) {
        if (match.status == 'IN_PROGRESS') {
          active++;
        }
      }
    }
    return active;
  }

  groupOnlyHasFinishedRounds(group: Group): boolean {
    let allFinished = true;
    for (const round of group.rounds) {
      allFinished &&= round.status == 'FINISHED';
    }
    return allFinished;
  }

  matchResultDialog = inject(MatDialog);
  courtSelectionDialog = inject(MatDialog);
  counterSelectionDialog = inject(MatDialog);

  editResult(match: Match, event: Event, group: Group, round: Round) {
    this.matchResultDialog.open(MatchResultComponent, {
      data: {match: match, tournament: this.tournament, event: event, group: group, round: round},
      minWidth: '800px'
    }).afterClosed().subscribe(result => {
      if (result != undefined) {
        this.loadTournament(this.tournamentService.saveResult(this.tournament.id, result.matchId, result));
      }
    });
  }

  checkWinner(match: Match): number {
    if (match.games.length == 0) return 0;
    if (match.games.length == 3) {
      if (match.games[2].score1 > match.games[2].score2) {
        return 1;
      } else {
        return 2;
      }
    }
    if (match.games[1].score1 > match.games[1].score2) {
      return 1;
    } else {
      return 2;
    }
  }

  checkRoundComplete(round: Round) {
    let complete: boolean = true;
    for (let match of round.matches) {
      complete &&= match.status == 'FINISHED';
    }

    return complete;
  }

  protected readonly TournamentEvent = Event;

  getEventMatchCount(event: Event) {
    let count = 0;
    for (let group of event.groups) {
      let numTeams = group.teams.length;
      let rounds = numTeams <= 4 ? 3 : 4;
      let matchesPerRound = Math.trunc(numTeams / 2);
      count += (rounds * matchesPerRound);
    }
    return count;
  }

  getTournamentMatchCount(tournament: Tournament) {
    let count = 0;
    for (let event of tournament.events) {
      count += this.getEventMatchCount(event);
    }
    return count;
  }

  changeCounter(match: Match) {
    this.counterSelectionDialog.open(CounterSelectionComponent, {
      data: {
        match: match,
        availableCounters: this.getAvailableCounters(match)
      },
      minWidth: '800px',
      minHeight: '250px'
    }).afterClosed().subscribe(result => {
      if (result != undefined) {
        this.loadTournament(this.tournamentService.updateCounter(this.tournament.id, match.id, result.counter));
      }
    });

  }

  private loadTournament(
    observable: Observable<Tournament>,
    afterLoad?: (tournament: Tournament) => void
  ): Subscription {
    return observable.subscribe(data => {
      this.tournament = data;
      this.updateMatchAvailability();
      afterLoad?.(data);  // Optional chaining
    });
  }

  private updateMatchAvailability() {
    for (const event of this.tournament.events) {
      for (const group of event.groups) {
        for (const round of group.rounds) {
          for (const match of round.matches) {
            let canStart = true;
            let matchPlayersThatArePlaying = this.matchPlayersThatArePlaying(match);
            let matchPlayersThatAreCounting = this.matchPlayersThatAreCounting(match);

            let cantStartReason = "";

            if (matchPlayersThatArePlaying.length > 0) {
              canStart = false;
              cantStartReason = this.joinWithEn(matchPlayersThatArePlaying.map(m => this.getTournamentPlayerFromId(m).name));
              if (matchPlayersThatArePlaying.length == 1) {
                cantStartReason += " is al aan het spelen";
              } else {
                cantStartReason += " zijn al aan het spelen";
              }
            }

            if (matchPlayersThatAreCounting.length > 0) {
              canStart = false;
              if (cantStartReason.length > 0) {
                cantStartReason += " en ";
              }
              cantStartReason += this.joinWithEn(matchPlayersThatAreCounting.map(m => this.getTournamentPlayerFromId(m).name));
              if (matchPlayersThatAreCounting.length == 1) {
                cantStartReason += " is aan het tellen";
              } else {
                cantStartReason += " zijn aan het tellen";
              }
            }

            match.canStart = canStart;
            match.cantStartReason = cantStartReason;
          }
        }
      }
    }
  }

  joinWithEn(items: string[], separator = ", "): string {
    const len = items.length;

    if (len === 0) return "";
    if (len === 1) return items[0];
    if (len === 2) return items.join(" en ");

    return items.slice(0, -1).join(separator) + `${separator}and ${items[len - 1]}`;
  }
}

class ActiveMatch {
  constructor(match: Match, round: Round, group: Group, event: Event) {
    this.match = match;
    this.round = round;
    this.group = group;
    this.event = event;
  }

  match: Match;
  round: Round;
  group: Group;
  event: Event;
}
